import React from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { ShoppingCart, User, Globe, LogOut, Package, ShieldCheck, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { Toaster } from "./ui/sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Layout() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLinks = ({ onClick }) => (
    <>
      <Link to="/" onClick={onClick} className="hover:text-[#8D1B3D]" data-testid="nav-home">{t("nav.home")}</Link>
      <Link to="/shop" onClick={onClick} className="hover:text-[#8D1B3D]" data-testid="nav-shop">{t("nav.shop")}</Link>
      <Link to="/projects-enquiry" onClick={onClick} className="hover:text-[#8D1B3D]" data-testid="nav-projects">{t("nav.projects")}</Link>
      <Link to="/jobs" onClick={onClick} className="hover:text-[#8D1B3D]" data-testid="nav-jobs">{t("nav.jobs")}</Link>
      <Link to="/talent-pool" onClick={onClick} className="hover:text-[#8D1B3D]" data-testid="nav-talent">{t("nav.talent")}</Link>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img src="/logo-wordmark.png" alt="Al Omarah" className="h-9 sm:h-10 w-auto object-contain" />
            <div className="leading-tight hidden md:block ms-2 ps-3 border-s border-slate-200">
              <div className="label-mono text-slate-500">{t("tagline")}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium">
            <NavLinks />
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 hover:border-slate-900 text-sm font-semibold"
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4" />
              {lang === "en" ? "AR" : "EN"}
            </button>

            <Link to="/cart" className="relative p-2 hover:bg-slate-100" data-testid="nav-cart">
              <ShoppingCart className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-1 -end-1 bg-[#8D1B3D] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 hover:bg-slate-100" data-testid="account-menu">
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs text-slate-500">{user.email}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                    <Package className="w-4 h-4 me-2" /> {t("nav.myOrders")}
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <ShieldCheck className="w-4 h-4 me-2" /> {t("nav.admin")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 me-2" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline-block">
                <Button className="btn-maroon rounded-none px-5 py-2 text-sm">{t("nav.login")}</Button>
              </Link>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 hover:bg-slate-100"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 px-4 py-4 flex flex-col gap-3 text-sm font-medium">
            <NavLinks onClick={() => setMobileOpen(false)} />
            {!user && <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-maroon px-4 py-2 inline-block w-fit text-white">{t("nav.login")}</Link>}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-[#0F172A] text-white mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-4 gap-10">
          <div>
            <div className="bg-white p-3 inline-block">
              <img src="/logo-wordmark.png" alt="Al Omarah" className="h-10 w-auto object-contain" />
            </div>
            <div className="label-mono text-slate-400 mt-3">{t("tagline")}</div>
            <div className="text-sm text-slate-300 mt-4">{t("footer.address")}</div>
            <a href="https://www.alomarah.com" target="_blank" rel="noreferrer" className="block text-sm text-slate-300 mt-2 hover:text-white">www.alomarah.com</a>
            <a href="mailto:info@alomarah.com" className="block text-sm text-slate-300 mt-1 hover:text-white">info@alomarah.com</a>
            <a href="tel:+97433732203" className="block text-sm text-slate-300 mt-1 hover:text-white">+974 3373 2203</a>
          </div>
          <div>
            <div className="label-mono text-slate-400 mb-3">{t("nav.shop")}</div>
            <ul className="space-y-2 text-sm text-slate-200">
              <li><Link to="/shop?category=plumbing">{t("categories.plumbing")}</Link></li>
              <li><Link to="/shop?category=sanitary">{t("categories.sanitary")}</Link></li>
              <li><Link to="/shop?category=hardware">{t("categories.hardware")}</Link></li>
              <li><Link to="/shop?category=building">{t("categories.building")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-mono text-slate-400 mb-3">{t("nav.services")}</div>
            <ul className="space-y-2 text-sm text-slate-200">
              <li><Link to="/projects-enquiry">{t("nav.projects")}</Link></li>
              <li><Link to="/jobs">{t("nav.jobs")}</Link></li>
              <li><Link to="/talent-pool">{t("nav.talent")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-mono text-slate-400 mb-3">Contact</div>
            <ul className="space-y-2 text-sm text-slate-200">
              <li><a href="tel:+97433732203" className="hover:text-white">+974 3373 2203</a></li>
              <li><a href="mailto:info@alomarah.com" className="hover:text-white">info@alomarah.com</a></li>
              <li><a href="https://wa.me/97433732203" target="_blank" rel="noreferrer" className="hover:text-white">WhatsApp</a></li>
            </ul>
            <div className="mt-5 flex items-center gap-3">
              <a href="https://facebook.com/alomarah" target="_blank" rel="noreferrer" aria-label="Facebook" className="w-9 h-9 border border-slate-700 hover:bg-[#8D1B3D] hover:border-[#8D1B3D] flex items-center justify-center transition-colors" data-testid="footer-fb">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.49-3.92 3.78-3.92 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.79 8.43-4.94 8.43-9.94z"/></svg>
              </a>
              <a href="https://instagram.com/alomarah" target="_blank" rel="noreferrer" aria-label="Instagram" className="w-9 h-9 border border-slate-700 hover:bg-[#8D1B3D] hover:border-[#8D1B3D] flex items-center justify-center transition-colors" data-testid="footer-ig">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.16c3.2 0 3.58 0 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.34 4.14.63a5.86 5.86 0 0 0-2.13 1.38A5.86 5.86 0 0 0 .63 4.14C.34 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.27 2.15.56 2.91a5.86 5.86 0 0 0 1.38 2.13 5.86 5.86 0 0 0 2.13 1.38c.76.29 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.27 2.91-.56a5.86 5.86 0 0 0 2.13-1.38 5.86 5.86 0 0 0 1.38-2.13c.29-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.27-2.15-.56-2.91a5.86 5.86 0 0 0-1.38-2.13A5.86 5.86 0 0 0 19.86.63C19.1.34 18.22.13 16.95.07 15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"/></svg>
              </a>
              <a href="https://wa.me/97433732203" target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 border border-slate-700 hover:bg-[#8D1B3D] hover:border-[#8D1B3D] flex items-center justify-center transition-colors" data-testid="footer-wa">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.555-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l.46.738-1.034 3.788 3.553-.985zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.296-.149-1.758-.868-2.03-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01a1.092 1.092 0 0 0-.793.372c-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@alomarah" target="_blank" rel="noreferrer" aria-label="TikTok" className="w-9 h-9 border border-slate-700 hover:bg-[#8D1B3D] hover:border-[#8D1B3D] flex items-center justify-center transition-colors" data-testid="footer-tt">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.92a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z"/></svg>
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Al Omarah Trading & Contracting · www.alomarah.com · {t("footer.rights")}
        </div>
      </footer>

      <Toaster richColors position={lang === "ar" ? "top-left" : "top-right"} />
    </div>
  );
}
